import { useEffect, useRef } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import RequireFacility from "../components/auth/RequireFacility";
import FacilityDeactivatedPage from "../components/shared/FacilityDeactivatedPage";
import NotFound from "../components/shared/NotFoundPage";
import { useTelemetryStream } from "../data/hooks/useTelemetryStream";
import { useCurrentRole } from "../data/hooks/useCurrentRole";
import { useFacilityDeactivation } from "../hooks/useActiveFacilityDeactivation";
import { isPlatformAdmin } from "../lib/rbac";
import { useTelemetryConnectionStore } from "../stores/useTelemetryConnectionStore";
import { createStaleInvalidationHandler } from "../lib/telemetryInvalidation";
import {
  createTelemetryPatchHandler,
  refetchDirectPatchEntitiesOnReconnect,
  type TelemetryEventPayload,
} from "../lib/telemetryPatch";

interface FacilityTelemetryOutletProps {
  facilityId: number;
}

interface FacilityLayoutInnerProps {
  orgId: number;
  facilityId: number;
}

// React Router does not remount a route element by default when only a
// dynamic param changes and the same route still matches (e.g. navigating
// from /facilities/1/overview to /facilities/2/overview) — it reuses the
// instance and re-renders with the new param. That's the wrong behavior
// here: switching facilities must tear down and reopen the telemetry
// connection, not silently keep it pointed at the old facility. Keying
// FacilityLayoutInner on facilityId (in FacilityLayout below) forces the
// intended remount — which also means the invalidation handler created here
// is naturally rebuilt per facility, not stale-scoped across a switch.
//
// Rendered only once RequireFacility (in FacilityLayoutInner) lets it
// through, so no telemetry stream opens for a facility the URL names but
// the user can't access — previously that 404'd and reconnected forever.
const FacilityTelemetryOutlet: React.FC<FacilityTelemetryOutletProps> = ({
  facilityId,
}) => {
  const queryClient = useQueryClient();
  const handleStaleEventRef = useRef(
    createStaleInvalidationHandler(queryClient, facilityId),
  );
  const handleTelemetryEventRef = useRef(
    createTelemetryPatchHandler(queryClient, facilityId),
  );
  // Tracks whether onConnected has already fired once, so the refetch below
  // (which would duplicate the page's own initial mount-time fetch) is
  // skipped for the very first connect — only a genuine reconnect refetches.
  const hasConnectedOnceRef = useRef(false);

  // Facility lifecycle (JAA-1117): Joules platform admins keep view-only
  // access to a deactivated facility's history. GET /organizations returns
  // deactivated facilities to them alone, flagged by status. A deactivated
  // facility has no live data, so no stream is opened for it — an idle
  // connection would only show a misleading "Live" indicator.
  const viewerIsPlatformAdmin = isPlatformAdmin(useCurrentRole());
  const { isDeactivated: knownDeactivated } = useFacilityDeactivation(facilityId);
  const streamEnabled = !(viewerIsPlatformAdmin && knownDeactivated);

  // event:bye is already fully handled at the connection-lifecycle level by
  // useTelemetryStream itself — no dispatch needed for it here.
  useTelemetryStream(facilityId, {
    onEvent: (eventType, data) => {
      if (eventType === "stale") {
        handleStaleEventRef.current();
      } else if (eventType === "telemetry") {
        handleTelemetryEventRef.current(data as TelemetryEventPayload);
      }
    },
    // JAA-1095: the stream never replays missed events, so systemStatus and
    // facilityOverview must be re-fetched on every reconnect — otherwise
    // they're silently stuck on stale data after a drop, the scheduled
    // 30-min token-TTL close, or a tab regaining visibility. The
    // stale-driven entities (facilityKpis, systemComponentComparison, etc.)
    // need the same resync — a snapshot landing during the disconnect gap
    // has its event:stale lost too, since the stream has no backlog (PR #323
    // review) — reusing the stale handler gives them the same scoping and
    // throttle it already applies to a live event:stale.
    onConnected: () => {
      if (hasConnectedOnceRef.current) {
        refetchDirectPatchEntitiesOnReconnect(queryClient, facilityId);
        handleStaleEventRef.current();
      }
      hasConnectedOnceRef.current = true;
    },
    enabled: streamEnabled,
  });

  // A live bye{reason:"deactivated"} only ever updates this store
  // (useTelemetryStream's own handling) — previously nothing read it back
  // out, so a tab already open on the facility kept rendering frozen,
  // increasingly stale content under nothing more than a small connection
  // badge change, with no clear signal the facility is actually gone until
  // the next REST call happened to 403 (which push otherwise makes rare).
  // Checked here, once, so every child route reacts to the same terminal
  // state TelemetryConnectionIndicator already uses — instantly, not on
  // whatever REST call happens to fire next.
  const telemetryConnectionState = useTelemetryConnectionStore(
    (connectionStore) => connectionStore.connections[facilityId]?.state,
  );

  // The stream just learned the facility is deactivated (live bye, or a 403
  // on connect) — the organizations list, which carries status and
  // deactivated_at, is now stale. Refetching it is what moves a platform
  // admin into view-only mode (banner, capped dates, no stream); for
  // everyone else it drops the facility from the sidebar and switcher.
  useEffect(() => {
    if (telemetryConnectionState === "deactivated") {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    }
  }, [telemetryConnectionState, queryClient]);

  // Platform admins never get the blocking page — they stay on the facility,
  // which turns view-only once the refetch above lands.
  const blockedAsDeactivated = telemetryConnectionState === "deactivated" && !viewerIsPlatformAdmin;

  if (telemetryConnectionState === "not_found") {
    return <NotFound message="This facility no longer exists." />;
  }
  return blockedAsDeactivated ? <FacilityDeactivatedPage /> : <Outlet />;
};

const FacilityLayoutInner: React.FC<FacilityLayoutInnerProps> = ({
  orgId,
  facilityId,
}) => (
  <RequireFacility orgId={orgId} facilityId={facilityId}>
    <FacilityTelemetryOutlet facilityId={facilityId} />
  </RequireFacility>
);

const FacilityLayout: React.FC = () => {
  const { orgId, facilityId } = useParams();
  return (
    <FacilityLayoutInner
      key={facilityId}
      orgId={Number(orgId)}
      facilityId={Number(facilityId)}
    />
  );
};

export default FacilityLayout;
